<template>
    <div>
        <!-- Most Confused Items Section -->
        <div v-if="type === 'confused_with' && mostConfusedPairsData.length > 0" style="margin-bottom: 30px; padding: 20px; background-color: #f5f5f5; border-radius: 4px;">
            <div style="font-size: 1.25rem; color: #666; margin-bottom: 15px; font-weight: 500;">
                {{ $t('confusion_matrix.most_confused_items') }}
            </div>
            <row>
                <col4 v-for="(pair, index) in mostConfusedPairsData" :key="index" style="margin-bottom: 20px;">
                    <div style="background-color: white; padding: 15px; border-radius: 4px; border: 1px solid #ddd;">
                        <div style="font-weight: 500; margin-bottom: 10px; text-align: center; color: #333;">
                            {{ pair.originalLabel }} → {{ pair.confusedLabel }}
                            <span style="color: #666; font-size: 0.9em;">({{ pair.count }})</span>
                        </div>
                        <row>
                            <col6>
                                <div style="margin-bottom: 5px; font-size: 0.85em; color: #666; text-align: center;">
                                    Original
                                </div>
                                <div v-for="(example, idx) in pair.originalExamples.slice(0, 3)" :key="idx" style="margin-bottom: 10px;">
                                    <img 
                                        v-if="example.data" 
                                        :src="'data:image/png;base64, ' + example.data" 
                                        style="width: 100%; max-height: 100px; object-fit: contain; border: 1px solid #ddd; border-radius: 2px;"
                                    />
                                    <div v-else style="width: 100%; height: 100px; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 2px; color: #999;">
                                        Loading...
                                    </div>
                                </div>
                            </col6>
                            <col6>
                                <div style="margin-bottom: 5px; font-size: 0.85em; color: #666; text-align: center;">
                                    Confused
                                </div>
                                <div v-for="(example, idx) in pair.confusedExamples.slice(0, 3)" :key="idx" style="margin-bottom: 10px;">
                                    <img 
                                        v-if="example.data" 
                                        :src="'data:image/png;base64, ' + example.data" 
                                        style="width: 100%; max-height: 100px; object-fit: contain; border: 1px solid #ddd; border-radius: 2px;"
                                    />
                                    <div v-else style="width: 100%; height: 100px; background-color: #f0f0f0; display: flex; align-items: center; justify-content: center; border: 1px solid #ddd; border-radius: 2px; color: #999;">
                                        Loading...
                                    </div>
                                </div>
                            </col6>
                        </row>
                    </div>
                </col4>
            </row>
        </div>
        <v-simple-table v-if="has_named_keys">
            <crops :model_id="model_id" :type="type" :title="type" :items="items_dict"></crops>
        </v-simple-table>
        <v-simple-table v-else>
            <crops :model_id="model_id" :type="type" v-for="pred_label in item_labels" :key="pred_label"
                   :title="pred_label" :items="items_dict[pred_label]"></crops>
        </v-simple-table>
    </div>
</template>

<script>
  import Crops from "@/components/confusion_matrix/Crops";
  import Row from "@/components/layout/Row";
  import Col4 from "@/components/layout/Col4";
  import Col6 from "@/components/layout/Col6";
  
  export default {
    name: "ConfusionMatrixItems",
    components: {Crops, Row, Col4, Col6},
    props: {
      model_id: {required: true},
      type: {required: true},
      items: {required: true},
      labels_dict: {required: true}
    },
    data() {
      return {
        mostConfusedPairsData: []
      }
    },
    computed: {
      enriched_items() {
        this.items.forEach(item=>{
          if(item.label_id) {
            item.label_name = this.labels_dict[item.label_id].name;
          }
          if(item.pred_label_id) {
            item.pred_label_name = this.labels_dict[item.pred_label_id].name;
          }
          return item;
        });
        return this.items;
      },
      has_named_keys() {
        if(this.enriched_items.length === 0) {
          return false;
        }

        return !this.enriched_items[0].pred_label_name;
      },
      items_dict() {
        if(this.enriched_items.length === 0) {
          return []
        }

        if(!this.enriched_items[0].pred_label_name) {
          return this.enriched_items;
        }

        let items_dict = {}
        this.enriched_items.forEach((item) => {
          let pred_label_name = item.pred_label_name;
          if(Object.keys(items_dict).indexOf(pred_label_name) === -1) {
            items_dict[pred_label_name] = []
          }
          items_dict[item.pred_label_name].push(item);
        })

        return items_dict;
      },
      item_labels() {
        return Object.keys(this.items_dict);
      },
      mostConfusedPairs() {
        if(this.type !== 'confused_with' || this.enriched_items.length === 0) {
          return [];
        }

        // Group items by (label_id, pred_label_id) pairs
        let pairsMap = {};
        this.enriched_items.forEach(item => {
          if(item.label_id && item.pred_label_id) {
            let key = `${item.label_id}_${item.pred_label_id}`;
            if(!pairsMap[key]) {
              pairsMap[key] = {
                originalLabel: item.label_name || this.labels_dict[item.label_id]?.name || 'Unknown',
                confusedLabel: item.pred_label_name || this.labels_dict[item.pred_label_id]?.name || 'Unknown',
                originalLabelId: item.label_id,
                confusedLabelId: item.pred_label_id,
                items: [],
                count: 0
              };
            }
            pairsMap[key].items.push(item);
            pairsMap[key].count++;
          }
        });

        // Convert to array and sort by count
        let pairs = Object.values(pairsMap);
        pairs.sort((a, b) => b.count - a.count);

        // Take top 3
        return pairs.slice(0, 3);
      }
    },
    watch: {
      mostConfusedPairs: {
        handler(newPairs) {
          if(newPairs.length > 0) {
            this.loadExamplesForPairs(newPairs);
          }
        },
        immediate: true
      }
    },
    methods: {
      async loadExamplesForPairs(pairs) {
        // Initialize pairs data
        this.mostConfusedPairsData = pairs.map(pair => ({
          ...pair,
          originalExamples: [],
          confusedExamples: []
        }));

        // Load examples for each pair
        for(let i = 0; i < this.mostConfusedPairsData.length; i++) {
          let pair = this.mostConfusedPairsData[i];
          
          // Load confused examples from the misclassified items
          let exampleItems = pair.items.slice(0, 3);
          for(let item of exampleItems) {
            try {
              let crop_data = await this.$api.asset.get_crop_for_conf_items('confused_with', JSON.stringify([item]));
              if(crop_data.crops && crop_data.crops.length > 0) {
                let crop = {
                  data: crop_data.crops[0].crop,
                  asset_id: crop_data.crops[0].asset_id
                };
                // These are the misclassified items (confused examples)
                pair.confusedExamples.push(crop);
              } else {
                let emptyCrop = { data: null, asset_id: item.asset_id };
                pair.confusedExamples.push(emptyCrop);
              }
            } catch(e) {
              let emptyCrop = { data: null, asset_id: item.asset_id };
              pair.confusedExamples.push(emptyCrop);
            }
          }

          // Load original examples - get example crops for correctly labeled items with the original label
          let project_id = sessionStorage.getItem('project_id');
          for(let j = 0; j < 3; j++) {
            try {
              let crop_data = await this.$api.label.get_crop(pair.originalLabel, project_id);
              if(crop_data.crop) {
                let crop = {
                  data: crop_data.crop,
                  asset_id: crop_data.asset_id
                };
                pair.originalExamples.push(crop);
              } else {
                pair.originalExamples.push({ data: null, asset_id: null });
              }
            } catch(e) {
              pair.originalExamples.push({ data: null, asset_id: null });
            }
          }
        }

        // Force reactivity update
        this.$forceUpdate();
      }
    },
    mounted() {
      if(this.type === 'confused_with' && this.mostConfusedPairs.length > 0) {
        this.loadExamplesForPairs(this.mostConfusedPairs);
      }
    }
  }
</script>

<style scoped>

</style>